<template>
  <div class="roster-management">
    <div class="roster-header">
      <h3 class="roster-title">⚡ Управление составами команды</h3>
      <a-button type="primary" @click="showCreateRosterModal">
        ➕ Создать состав
      </a-button>
    </div>

    <a-spin :spinning="loading">
      <div v-if="rosters.length > 0" class="rosters-grid">
        <div v-for="item in rosters" :key="item.id" class="roster-card">
          <div class="roster-card-header">
            <div class="roster-card-title">
              <h4>{{ item.name }}</h4>
              <a-tag v-if="item.is_default" color="blue" class="default-tag">
                ⭐ Основной
              </a-tag>
            </div>
            <div class="roster-card-count">
              <span class="player-count">👥 {{ item.player_count }}</span>
            </div>
          </div>

          <div v-if="item.description" class="roster-card-description">
            {{ item.description }}
          </div>

          <div class="roster-card-actions">
            <a-button 
              type="default" 
              size="small" 
              @click="editRoster(item)"
              class="action-btn"
            >
              ✏️ Редактировать
            </a-button>
            <a-button 
              type="primary" 
              size="small" 
              @click="manageRosterPlayers(item)"
              class="action-btn"
            >
              👥 Игроки ({{ item.player_count }})
            </a-button>
            <a-popconfirm
              title="Вы уверены, что хотите удалить этот состав?"
              ok-text="Да"
              cancel-text="Нет"
              @confirm="deleteRoster(item.id)"
            >
              <a-button 
                type="default" 
                danger 
                size="small"
                class="action-btn"
              >
                🗑️ Удалить
              </a-button>
            </a-popconfirm>
          </div>
        </div>
      </div>

      <a-empty 
        v-else 
        description="Нет составов" 
        class="empty-state"
      >
        <template #image>
          <div class="empty-icon">👥</div>
        </template>
        <a-button type="primary" @click="showCreateRosterModal">
          Создать первый состав
        </a-button>
      </a-empty>
    </a-spin>

    <!-- Create/Edit Roster Modal -->
    <a-modal
      v-model:visible="rosterModalVisible"
      :title="editingRoster ? 'Редактировать состав' : 'Создать состав'"
      @ok="saveRoster"
      @cancel="closeRosterModal"
      :confirm-loading="savingRoster"
    >
      <a-form :model="rosterForm" layout="vertical">
        <a-form-item
          label="Название"
          required
          :rules="[{ required: true, message: 'Введите название состава' }]"
        >
          <a-input v-model:value="rosterForm.name" placeholder="Например: Первый состав" />
        </a-form-item>

        <a-form-item label="Описание">
          <a-textarea
            v-model:value="rosterForm.description"
            :rows="3"
            placeholder="Дополнительная информация о составе"
          />
        </a-form-item>

        <a-form-item>
          <a-checkbox v-model:checked="rosterForm.is_default">
            Основной состав
          </a-checkbox>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- Manage Players Modal -->
    <a-modal
      v-model:visible="playersModalVisible"
      :title="`👥 Игроки состава: ${selectedRoster?.name || ''}`"
      width="900px"
      @cancel="closePlayersModal"
      :footer="null"
      class="players-modal"
    >
      <a-spin :spinning="loadingPlayers">
        <div class="roster-players-management">
          <!-- Add Players Section -->
          <div class="add-players-section">
            <h4 class="section-title">➕ Добавить игроков в состав</h4>
            <a-select
              v-model:value="selectedPlayersToAdd"
              mode="multiple"
              placeholder="Выберите игроков для добавления"
              class="players-select"
              :options="availablePlayers"
              :field-names="{ label: 'label', value: 'value' }"
            />
            <a-button
              type="primary"
              @click="addPlayersToRoster"
              :disabled="selectedPlayersToAdd.length === 0"
              :loading="addingPlayers"
              block
              class="add-btn"
            >
              ➕ Добавить выбранных игроков ({{ selectedPlayersToAdd.length }})
            </a-button>
          </div>

          <a-divider />

          <!-- Current Players List -->
          <div class="current-players">
            <h4 class="section-title">📋 Текущие игроки состава ({{ rosterPlayers.length }})</h4>
            <div v-if="rosterPlayers.length > 0" class="players-list">
              <div 
                v-for="item in rosterPlayers" 
                :key="item.id" 
                class="player-item"
              >
                <div class="player-info">
                  <a-avatar :src="item.photo_url" :size="40" class="player-avatar">
                    {{ item.last_name?.[0] }}{{ item.first_name?.[0] }}
                  </a-avatar>
                  <div class="player-details">
                    <div class="player-name">
                      {{ item.last_name }} {{ item.first_name }}
                      <a-tag v-if="item.captain_status === 'captain'" color="gold">
                        👑 Капитан
                      </a-tag>
                      <a-tag v-if="item.captain_status === 'assistant'" color="orange">
                        🅰️ Ассистент
                      </a-tag>
                    </div>
                    <div class="player-meta">
                      № {{ item.number || '-' }} • {{ getPositionLabel(item.position) }}
                    </div>
                  </div>
                </div>
                <a-popconfirm
                  title="Удалить игрока из состава?"
                  ok-text="Да"
                  cancel-text="Нет"
                  @confirm="removePlayerFromRoster(item.id)"
                >
                  <a-button danger size="small" class="remove-btn">
                    🗑️ Удалить
                  </a-button>
                </a-popconfirm>
              </div>
            </div>
            <a-empty v-else description="Нет игроков в составе" class="empty-players">
              <template #image>
                <div class="empty-icon">🏒</div>
              </template>
            </a-empty>
          </div>
        </div>
      </a-spin>
    </a-modal>
  </div>
</template>

<script>
import { 
  message,
  Card,
  Button,
  List,
  Spin,
  Empty,
  Modal,
  Form,
  Input,
  Checkbox,
  Select,
  Divider,
  Avatar,
  Tag,
  Popconfirm
} from 'ant-design-vue';
import { API_URL } from '../config';

export default {
  name: 'TeamRosterManagement',
  components: {
    'a-card': Card,
    'a-button': Button,
    'a-list': List,
    'a-list-item': List.Item,
    'a-list-item-meta': List.Item.Meta,
    'a-spin': Spin,
    'a-empty': Empty,
    'a-modal': Modal,
    'a-form': Form,
    'a-form-item': Form.Item,
    'a-input': Input,
    'a-textarea': Input.TextArea,
    'a-checkbox': Checkbox,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-divider': Divider,
    'a-avatar': Avatar,
    'a-tag': Tag,
    'a-popconfirm': Popconfirm
  },
  props: {
    teamId: {
      type: Number,
      required: true
    },
    token: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      rosters: [],
      rosterModalVisible: false,
      playersModalVisible: false,
      editingRoster: null,
      selectedRoster: null,
      rosterForm: {
        name: '',
        description: '',
        is_default: false
      },
      savingRoster: false,
      loadingPlayers: false,
      rosterPlayers: [],
      teamPlayers: [],
      selectedPlayersToAdd: [],
      addingPlayers: false
    };
  },
  computed: {
    availablePlayers() {
      const rosterPlayerIds = new Set(this.rosterPlayers.map(p => p.id));
      return this.teamPlayers
        .filter(p => !rosterPlayerIds.has(p.id))
        .map(p => ({
          value: p.id,
          label: `${p.first_name} ${p.last_name} ${p.number ? `(#${p.number})` : ''}`
        }));
    }
  },
  mounted() {
    if (this.teamId) {
      this.loadRosters();
      this.loadTeamPlayers();
    }
  },
  watch: {
    teamId(newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.loadRosters();
        this.loadTeamPlayers();
      }
    }
  },
  methods: {
    async loadRosters() {
      this.loading = true;
      try {
        const response = await fetch(`${API_URL}/teams/${this.teamId}/rosters`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });

        if (response.ok) {
          this.rosters = await response.json();
        } else {
          const error = await response.json();
          message.error(error.message || 'Ошибка при загрузке составов');
        }
      } catch (error) {
        console.error('Error loading rosters:', error);
        message.error('Ошибка при загрузке составов');
      } finally {
        this.loading = false;
      }
    },

    async loadTeamPlayers() {
      try {
        const response = await fetch(`${API_URL}/teams/${this.teamId}/players`, {
          headers: { Authorization: `Bearer ${this.token}` }
        });

        if (response.ok) {
          const data = await response.json();
          console.log('Loaded team players:', data);
          
          // API возвращает объект с полем players, где players - объект с позициями
          // { players: { forward: [...], defender: [...], ... } }
          if (data.players && typeof data.players === 'object' && !Array.isArray(data.players)) {
            // Собираем всех игроков из всех позиций в один массив
            this.teamPlayers = Object.values(data.players).flat();
          } else if (Array.isArray(data.players)) {
            // Если players - массив
            this.teamPlayers = data.players;
          } else if (Array.isArray(data)) {
            // Если data сам массив
            this.teamPlayers = data;
          } else {
            this.teamPlayers = [];
          }
          
          console.log('Team players array:', this.teamPlayers);
        } else {
          const error = await response.json();
          console.error('Error response loading team players:', error);
          message.error(error.message || 'Ошибка при загрузке игроков команды');
        }
      } catch (error) {
        console.error('Error loading team players:', error);
        message.error('Ошибка при загрузке игроков команды');
      }
    },

    showCreateRosterModal() {
      this.editingRoster = null;
      this.rosterForm = {
        name: '',
        description: '',
        is_default: false
      };
      this.rosterModalVisible = true;
    },

    editRoster(roster) {
      this.editingRoster = roster;
      this.rosterForm = {
        name: roster.name,
        description: roster.description || '',
        is_default: roster.is_default === 1
      };
      this.rosterModalVisible = true;
    },

    async saveRoster() {
      if (!this.rosterForm.name.trim()) {
        message.error('Введите название состава');
        return;
      }

      this.savingRoster = true;
      try {
        const url = this.editingRoster
          ? `${API_URL}/teams/${this.teamId}/rosters/${this.editingRoster.id}`
          : `${API_URL}/teams/${this.teamId}/rosters`;

        const method = this.editingRoster ? 'PUT' : 'POST';

        const response = await fetch(url, {
          method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.token}`
          },
          body: JSON.stringify(this.rosterForm)
        });

        if (response.ok) {
          message.success(
            this.editingRoster ? 'Состав обновлен' : 'Состав создан'
          );
          this.closeRosterModal();
          this.loadRosters();
        } else {
          const error = await response.json();
          message.error(error.message || 'Ошибка при сохранении состава');
        }
      } catch (error) {
        console.error('Error saving roster:', error);
        message.error('Ошибка при сохранении состава');
      } finally {
        this.savingRoster = false;
      }
    },

    closeRosterModal() {
      this.rosterModalVisible = false;
      this.editingRoster = null;
      this.rosterForm = {
        name: '',
        description: '',
        is_default: false
      };
    },

    async deleteRoster(rosterId) {
      try {
        const response = await fetch(
          `${API_URL}/teams/${this.teamId}/rosters/${rosterId}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${this.token}` }
          }
        );

        if (response.ok) {
          message.success('Состав удален');
          this.loadRosters();
        } else {
          const error = await response.json();
          message.error(error.message || 'Ошибка при удалении состава');
        }
      } catch (error) {
        console.error('Error deleting roster:', error);
        message.error('Ошибка при удалении состава');
      }
    },

    async manageRosterPlayers(roster) {
      this.selectedRoster = roster;
      this.playersModalVisible = true;
      this.selectedPlayersToAdd = [];
      
      // Убедимся, что игроки команды загружены
      if (!this.teamPlayers || this.teamPlayers.length === 0) {
        await this.loadTeamPlayers();
      }
      
      await this.loadRosterPlayers(roster.id);
    },

    async loadRosterPlayers(rosterId) {
      this.loadingPlayers = true;
      try {
        const response = await fetch(
          `${API_URL}/teams/${this.teamId}/rosters/${rosterId}`,
          {
            headers: { Authorization: `Bearer ${this.token}` }
          }
        );

        if (response.ok) {
          const data = await response.json();
          this.rosterPlayers = data.players || [];
        } else {
          const error = await response.json();
          message.error(error.message || 'Ошибка при загрузке игроков');
        }
      } catch (error) {
        console.error('Error loading roster players:', error);
        message.error('Ошибка при загрузке игроков');
      } finally {
        this.loadingPlayers = false;
      }
    },

    async addPlayersToRoster() {
      if (!this.selectedRoster || this.selectedPlayersToAdd.length === 0) return;

      this.addingPlayers = true;
      try {
        const response = await fetch(
          `${API_URL}/teams/${this.teamId}/rosters/${this.selectedRoster.id}/players`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${this.token}`
            },
            body: JSON.stringify({ player_ids: this.selectedPlayersToAdd })
          }
        );

        if (response.ok) {
          message.success('Игроки добавлены в состав');
          this.selectedPlayersToAdd = [];
          await this.loadRosterPlayers(this.selectedRoster.id);
          await this.loadRosters();
        } else {
          const error = await response.json();
          message.error(error.message || 'Ошибка при добавлении игроков');
        }
      } catch (error) {
        console.error('Error adding players to roster:', error);
        message.error('Ошибка при добавлении игроков');
      } finally {
        this.addingPlayers = false;
      }
    },

    async removePlayerFromRoster(playerId) {
      if (!this.selectedRoster) return;

      try {
        const response = await fetch(
          `${API_URL}/teams/${this.teamId}/rosters/${this.selectedRoster.id}/players/${playerId}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${this.token}` }
          }
        );

        if (response.ok) {
          message.success('Игрок удален из состава');
          await this.loadRosterPlayers(this.selectedRoster.id);
          await this.loadRosters();
        } else {
          const error = await response.json();
          message.error(error.message || 'Ошибка при удалении игрока');
        }
      } catch (error) {
        console.error('Error removing player from roster:', error);
        message.error('Ошибка при удалении игрока');
      }
    },

    closePlayersModal() {
      this.playersModalVisible = false;
      this.selectedRoster = null;
      this.rosterPlayers = [];
      this.selectedPlayersToAdd = [];
    },

    getPositionLabel(position) {
      const positions = {
        forward: 'Нападающий',
        defender: 'Защитник',
        goalkeeper: 'Вратарь',
        coach: 'Тренер',
        service: 'Обслуживающий персонал'
      };
      return positions[position] || position;
    }
  }
};
</script>

<style scoped>
.roster-management {
  max-width: 1200px;
  margin: 0 auto;
}

/* Header */
.roster-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 0 4px;
}

.roster-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
}

/* Grid Layout */
.rosters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 20px;
  margin-top: 20px;
}

/* Roster Card */
.roster-card {
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 12px;
  padding: 20px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.roster-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  border-color: #1890ff;
}

.roster-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.roster-card-title {
  flex: 1;
}

.roster-card-title h4 {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #1a1a1a;
}

.default-tag {
  font-size: 12px;
}

.roster-card-count {
  margin-left: 12px;
}

.player-count {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background: #f0f5ff;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #1890ff;
}

.roster-card-description {
  font-size: 14px;
  color: #666;
  margin-bottom: 16px;
  line-height: 1.5;
}

.roster-card-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
}

.action-btn {
  flex: 1;
  min-width: 100px;
}

/* Empty State */
.empty-state {
  margin: 60px 0;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

/* Players Modal */
.add-players-section {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8f0fe 100%);
  padding: 24px;
  border-radius: 12px;
  margin-bottom: 24px;
}

.section-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 16px 0;
  color: #1a1a1a;
}

.players-select {
  width: 100%;
  margin-bottom: 16px;
}

.add-btn {
  height: 40px;
  font-size: 14px;
  font-weight: 500;
}

/* Players List */
.players-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 8px;
}

.players-list::-webkit-scrollbar {
  width: 6px;
}

.players-list::-webkit-scrollbar-track {
  background: #f0f0f0;
  border-radius: 3px;
}

.players-list::-webkit-scrollbar-thumb {
  background: #d0d0d0;
  border-radius: 3px;
}

.players-list::-webkit-scrollbar-thumb:hover {
  background: #b0b0b0;
}

.player-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.player-item:hover {
  background: #fafafa;
  border-color: #1890ff;
}

.player-info {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 16px;
}

.player-avatar {
  flex-shrink: 0;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
}

.player-details {
  flex: 1;
}

.player-name {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.player-meta {
  font-size: 14px;
  color: #666;
}

.remove-btn {
  flex-shrink: 0;
}

.empty-players .empty-icon {
  font-size: 36px;
}

/* Responsive */
@media (max-width: 768px) {
  .rosters-grid {
    grid-template-columns: 1fr;
  }

  .roster-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .roster-card-actions {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }

  .player-item {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .remove-btn {
    width: 100%;
  }
}
</style>

